import { useEffect, useRef } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { Customer } from '@/types/customer-type';
import { FbConversation } from '@/types/fb-type';
import { toast } from './use-toast';

export interface UseOrderConversationReturn {
  shouldShowChat: boolean;
  currentConversation: FbConversation | null;
  facebookId: string | null;
  customerName: string | null;
  isInactive: boolean;
}

export function useOrderConversation(
  customer: Customer | null,
  currentPageId?: string | null,
): UseOrderConversationReturn {
  const {
    currentConversation,
    loadingConversations,
    selectConversationById,
    selectConversation,
  } = useChat();

  const selectedConversationRef = useRef<string | null>(null);

  useEffect(() => {
    selectedConversationRef.current = null;
  }, [customer?.id]);

  useEffect(() => {
    if (!customer) {
      return;
    }

    const customerFacebookId = customer.facebookId;
    const customerConversationId = customer.facebookConversationId;
    const isCustomerFromAnotherPage = Boolean(
      customer.facebookPageId &&
      currentPageId &&
      customer.facebookPageId !== currentPageId,
    );
    const selectionKey = `${isCustomerFromAnotherPage ? 'cache' : 'live'}:${currentPageId ?? ''}:${customerConversationId ?? ''}`;

    if (!customerConversationId) {
      if (!customerFacebookId && selectedConversationRef.current !== customer.id) {
        toast({
          title: "Warning",
          description: `[OrderDetails] Customer ${customer.id} has not chat with us please inform them to chat with us !`,
          variant: "warning",
          duration: 0,
        });
        selectedConversationRef.current = customer.id;
      }
      return;
    }

    if (isCustomerFromAnotherPage) {
      if (selectedConversationRef.current !== selectionKey) {
        const cachedConversation = Object.assign(new FbConversation(), {
          id: customerConversationId,
          pageId: customer.facebookPageId,
          cacheOnly: true,
          snippet: '',
          updatedTime: customer.lastFacebookMessage ?? customer.updatedAt,
          messageCount: 0,
          unreadCount: 0,
          participants: [
            {
              id: customerFacebookId || customer.id,
              name: customer.name,
              username: '',
              profileImageUrl: customer.profileImageUrl ?? undefined,
              conversationId: customerConversationId,
            },
          ],
          lastCustomerMessageTime: customer.lastFacebookMessage,
          messagingWindow: 'CLOSED' as const,
        });
        selectedConversationRef.current = selectionKey;
        selectConversation(cachedConversation);
      }
      return;
    }

    if (loadingConversations) {
      return;
    }

    if (selectedConversationRef.current !== selectionKey) {
      selectedConversationRef.current = selectionKey;
      selectConversationById(customerConversationId);
    }
  }, [
    customer?.id,
    customer?.facebookId,
    customer?.facebookConversationId,
    customer?.facebookPageId,
    customer?.lastFacebookMessage,
    customer?.name,
    customer?.profileImageUrl,
    customer?.updatedAt,
    currentPageId,
    loadingConversations,
    selectConversation,
    selectConversationById,
  ]);

  const shouldShowChat = Boolean(
    currentConversation &&
    customer?.facebookConversationId &&
    currentConversation.id === customer.facebookConversationId,
  );
  const facebookId = customer?.facebookId || null;
  const customerName = customer?.name || null;
  const isInactive = Boolean(
    customer?.facebookPageId &&
    currentPageId &&
    customer.facebookPageId !== currentPageId,
  );
  return {
    shouldShowChat,
    currentConversation,
    facebookId,
    customerName,
    isInactive,
  };
}
